import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { OwnHistoriesDataSource } from './own-histories-datasource';

import { AuthService } from '@app/services/auth.service';

import { MatDialog } from '@angular/material/dialog';
import { Utility } from '@app/models/utility';
import { ReportService } from '@app/services/report.service';
import { Define } from 'src/app/models/define';

import htmlToPdfmake from 'html-to-pdfmake';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { PdfMakeService } from '@app/services/pdf-make.service';
import { getDoc } from 'firebase/firestore';

export interface KeyValue {
  id: Date;
  value: string;
}

@Component({
  selector: 'app-own-histories',
  templateUrl: './own-histories.component.html',
  styleUrls: ['./own-histories.component.css']
})
export class OwnHistoriesComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource: OwnHistoriesDataSource;
  dataSub$ = null;
  startDate: Date;
  endDate: Date;
  personalClosingDay: Define.closingDay;
  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  _selectMonth: Date;
  get selectMonth(): Date {
    return this._selectMonth;
  }
  set selectMonth(value: Date) {
    this._selectMonth = value;

    switch(this.personalClosingDay) {
      case Define.closingDay.Undefined:
      case Define.closingDay.DayOf15:
        this.startDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth() - 1, 16);
        this.endDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth(), 15);
        break;
      case Define.closingDay.DayOf10:
        this.startDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth() - 1, 11);
        this.endDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth(), 10);
        break;
      case Define.closingDay.DayOf20:
        this.startDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth() - 1, 21);
        this.endDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth(), 20);
        break;
      case Define.closingDay.DayOf25:
        this.startDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth() - 1, 26);
        this.endDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth(), 25);
        break;
      case Define.closingDay.EndOfMonth:
        this.startDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth(), 1);
        this.endDate = new Date(this._selectMonth.getFullYear(), this._selectMonth.getMonth() + 1, 0);// 翌月の1日前
        break;
    }

    if (this.dataSource !== undefined) {
      this.update();
    }
  }
  monthes: KeyValue[];

  constructor(
    public auth: AuthService,
    private reportService: ReportService,
    public dialog: MatDialog,
    private pdfMakeService: PdfMakeService
  ) {
    this.initializeDisplay();
  }
  ngOnInit() {
  }

  async initializeDisplay() {
    const currentUser = await this.auth.getUser();
    this.personalClosingDay = currentUser.closingDay ?? Define.closingDay.DayOf15; // DBに設定保存なければ15日締め

    const today = new Date();
    let startMonthNum = 0;
    if (today.getDate() <= 15) {
      // 15日以前なら前月16日から当月15日まで
      startMonthNum = today.getMonth();
    } else {
      // 16日以降なら当月16日から翌月15日まで
      startMonthNum = today.getMonth() + 1;
    }
    const beforeRange = 1;
    const startMonth = new Date(today.getFullYear(), startMonthNum - beforeRange, 16);
    this.monthes = new Array();
    for (let i = 0; i <= beforeRange; i++) {
      const month = new Date(startMonth);
      month.setMonth(month.getMonth() + i);
      this.monthes.push({ id: month, value: `${month.getFullYear()}年${month.getMonth() + 1}月` });
    }
    this.selectMonth = this.monthes[this.monthes.length - 1].id;

    // 表示リフレッシュ
    this.update();
  }

  async update() {
    const startDate = new Date(this.startDate);
    const endDate = new Date(this.endDate);
    startDate.setDate(startDate.getDate());
    endDate.setDate(endDate.getDate());
    const data = await this.reportService.loadHistories(await this.auth.getUser(), startDate, endDate);
    this.dataSource = new OwnHistoriesDataSource(data);
    this.dataSub$ = this.dataSource.connect();
  }
  async saveReport() {
    if (this.dataSource === undefined) {
      console.log('saveReport invalid data.');
      return;
    }
    const user = await this.auth.getUser();
    const userId = user.id;
    const teamDoc = await getDoc(user.team);
    const teamName = teamDoc.data()['name'];
    const result = await this.dataSource.data.createCsv();
    console.log(result);

    const startDateText = Utility.convertDateToDateText(this.startDate, true);
    const endDateText = Utility.convertDateToDateText(this.endDate, true);
    Utility.downloadText(result, `勤怠記録_${userId}_${user.displayName}_${teamName}_${startDateText}-${endDateText}.csv`);
  }

  async savePDFReport() {
    if (this.dataSource === undefined) {
      console.log('saveReport invalid data.');
      return;
    }

    const fontName = await this.pdfMakeService.ensureFonts();
    const user = await this.auth.getUser();
    const userId = user.id;
    const teamDoc = await getDoc(user.team);
    const teamName = teamDoc.data()['name'];
    let html = '';
    let lineNumber: number;
    [html, lineNumber] = await this.dataSource.data.createHTMLforPDF();

    const docDef: TDocumentDefinitions = {
      info: {
        title: `${teamName}_${userId}_${user.displayName}`,
        author: '夏原工業 勤怠管理システム',
        subject: '打刻レポート',
        keywords: '',
      },
      content: [htmlToPdfmake(html, {
        tableAutoSize: true
      })],
      defaultStyle: {
        font: fontName,
        fontSize: Math.min(480 / lineNumber, 10) // 改行数でフォント変更 最大は10で丸め
      },
      pageSize: 'A4'
    };
    this.pdfMakeService.createPdf(docDef).download(`${teamName}_${userId}_${user.displayName}.pdf`);
  }
}
